import React, { useState } from 'react';
import axios from 'axios';
import CurrencyInput from 'react-currency-input-field';
import { crops, weedControlMethodOptions } from './config';
import CurrentPracticesCropsQuestion from './CurrentPracticesCropsQuestion';
import CurrentPracticesWeedControlMethodQuestion from './CurrentPracticesWeedControlMethodQuestion';
import RenderError from '../../components/RenderError';
import Success from '../../../equipmentSurveys/components/Success';
import Submit from '../../../equipmentSurveys/components/Submit';

function CurrentPractices({
  applicationData: {
    aid,
    startDate,
    endDate,
    seasonVariantsByCrop,
    cropsToTreat,
    weedControlMethods,
    maintenanceHours,
    maintenanceFrequency,
    maintenanceCost,
    maintenanceProvider
  },
  setApplicationData,
  initialView,
  setView
}) {
  const [formData, setFormData] = useState({
    aid: aid ?? '',
    startDate: startDate ?? '',
    endDate: endDate ?? '',
    seasonVariantsByCrop: seasonVariantsByCrop ?? '',
    cropsToTreat: cropsToTreat ?? {},
    weedControlMethods: weedControlMethods ?? {},
    maintenanceHours: maintenanceHours ? parseInt(maintenanceHours) : 0,
    maintenanceFrequency: maintenanceFrequency ?? '',
    maintenanceCost: maintenanceCost ? parseFloat(maintenanceCost) : 0,
    maintenanceProvider: maintenanceProvider ?? ''
  });
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  const handleCheckCrop = (event) => {
    const { value } = event.target;

    if (value in formData.cropsToTreat) {
      let newFormData = formData.cropsToTreat;
      delete newFormData[value];
      setFormData({ ...formData, cropsToTreat: newFormData });
    } else {
      setFormData({
        ...formData,
        cropsToTreat: {
          ...formData.cropsToTreat,
          [value]: {
            crop: value === 'Other (please specify)' ? '' : value
          }
        }
      });
    }
  };

  const handleCheckMethod = (event) => {
    const { value } = event.target;

    if (value in formData.weedControlMethods) {
      let newFormData = formData.weedControlMethods;
      delete newFormData[value];
      setFormData({ ...formData, weedControlMethods: newFormData });
    } else {
      setFormData({
        ...formData,
        weedControlMethods: {
          ...formData.weedControlMethods,
          [value]: {
            method: value === 'Other (please specify)' ? '' : value
          }
        }
      });
    }
  };

  const handleCropInputChange = (event, crop) => {
    const { value, name } = event.target;

    setFormData({
      ...formData,
      cropsToTreat: {
        ...formData.cropsToTreat,
        [crop]: {
          ...formData.cropsToTreat[crop],
          [name]: value
        }
      }
    });
  };

  const handleMethodInputChange = (event, method) => {
    const { value, name } = event.target;

    setFormData({
      ...formData,
      weedControlMethods: {
        ...formData.weedControlMethods,
        [method]: {
          ...formData.weedControlMethods[method],
          [name]: value
        }
      }
    });
  };

  const handleInputChange = (event) => {
    const { value, name } = event.target;

    if (name.includes('_other')) {
      let key = name.replace('_other', '');
      setFormData({
        ...formData,
        [key]: {
          ...formData[key],
          ['Other (please specify)']: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setLoading(true);
    setErrors([]);

    axios
      .post(
        window.wlPipConfig.ajaxUrl +
          '?action=wl_fip_save_flame_weeding_current_practices',
        formData
      )
      .then((res) => {
        setLoading(false);
        setSuccess(res.data.data.message);
        setApplicationData(res.data.data.applicationData);
        setTimeout(() => setView(initialView), 1000);
      })
      .catch((err) => {
        setLoading(false);
        setErrors(err.response.data.data);
      });
  };

  return (
    <div className="fip-unit-form">
      <div className="fip-unit-form-fields">
        <div className="grid__col-xs-12 u--padding-left-0">
          <div className="form__field form__field--required">
            <label className="form__label u--margin-bottom-20">
              Typically, when do you conduct your first weed control treatment
              each year? When do you conduct your last weed control treatment
              each year?
            </label>
            <span>Start Date:</span>
            <input
              type="date"
              name="startDate"
              max="9999-12-31"
              value={formData.startDate}
              className="form__input form__input--lg u--margin-bottom-20"
              onChange={handleInputChange}
            />
            {errors.startDate && <RenderError error={errors.startDate} />}
            <span>End Date:</span>
            <input
              type="date"
              name="endDate"
              max="9999-12-31"
              value={formData.endDate}
              className="form__input form__input--lg u--margin-bottom-20"
              onChange={handleInputChange}
            />
            {errors.endDate && <RenderError error={errors.endDate} />}
            <p>
              Please specify if this season varies for different crops.
              <textarea
                className="form__textarea"
                name="seasonVariantsByCrop"
                value={formData.seasonVariantsByCrop}
                onChange={handleInputChange}
              ></textarea>
            </p>
            {errors.seasonVariantsByCrop && (
              <RenderError error={errors.seasonVariantsByCrop} />
            )}
          </div>

          <CurrentPracticesCropsQuestion
            crops={crops}
            formData={formData}
            handleCheckCrop={handleCheckCrop}
            handleCropInputChange={handleCropInputChange}
            setFormData={setFormData}
            errors={errors}
          />

          <CurrentPracticesWeedControlMethodQuestion
            weedControlMethodOptions={weedControlMethodOptions}
            formData={formData}
            handleCheckMethod={handleCheckMethod}
            handleMethodInputChange={handleMethodInputChange}
            setFormData={setFormData}
            errors={errors}
          />

          <div className="form__field form__field--required">
            <label className="form__label" htmlFor="maintenanceHours">
              On average, how many hours of maintenance services does your weed
              control system require per year?
            </label>
            <input
              type="number"
              value={formData.maintenanceHours}
              className="form__input form__input--lg u--margin-bottom-10"
              name="maintenanceHours"
              onChange={handleInputChange}
            />
            {errors.maintenanceHours && (
              <RenderError error={errors.maintenanceHours} />
            )}
          </div>

          <div className="form__field form__field--required">
            <label className="form__label" htmlFor="maintenanceFrequency">
              How often is this maintenance required?
            </label>

            <div className="form__select form__select--lg">
              <select
                onChange={handleInputChange}
                defaultValue={formData.maintenanceFrequency}
                name="maintenanceFrequency"
              >
                <option value="">Please Select</option>
                {Array.from([
                  'Once per year',
                  'Twice per year',
                  '3 times per year',
                  '5+ times per year'
                ]).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              {errors.maintenanceFrequency && (
                <RenderError error={errors.maintenanceFrequency} />
              )}
            </div>
          </div>

          <div className="form__field form__field--required">
            <label className="form__label" htmlFor="maintenanceCost">
              What is the annual average cost of maintenance per year?
            </label>
            <CurrencyInput
              name="maintenanceCost"
              prefix="$"
              className="form__input form__input--lg u--margin-bottom-10"
              value={
                (formData.maintenanceCost !== null) &
                (formData.maintenanceCost !== undefined)
                  ? parseInt(formData.maintenanceCost)
                  : 0
              }
              onValueChange={(value, name) => {
                setFormData({
                  ...formData,
                  [name]: value
                });
              }}
            />
            {errors.maintenanceCost && (
              <RenderError error={errors.maintenanceCost} />
            )}
          </div>

          <div className="form__field form__field--required">
            <label
              className="form__label u--margin-right-20"
              htmlFor="maintenanceProvider"
            >
              Who typically performs engine maintenance? (select one)
            </label>
            {errors.maintenanceProvider && (
              <RenderError error={errors.maintenanceProvider} />
            )}
            <label className="form__radio">
              <input
                type="radio"
                name="maintenanceProvider"
                value="company"
                onChange={handleInputChange}
                checked={
                  formData.maintenanceProvider === 'company' && 'checked'
                }
              />
              My company or employee(s)
            </label>
            <label className="form__radio">
              <input
                type="radio"
                name="maintenanceProvider"
                value="dealership"
                onChange={handleInputChange}
                checked={
                  formData.maintenanceProvider === 'dealership' && 'checked'
                }
              />
              My dealership or local service center
            </label>
          </div>

          <Submit
            loading={loading}
            handleSubmit={handleSubmit}
            text="Save Section"
            errors={errors}
            disabled={!Boolean(aid)}
          />
          {success && <Success success={success} />}
        </div>
      </div>
    </div>
  );
}

CurrentPractices.displayName = 'CurrentPractices'; // because WebPack renames the function
export default CurrentPractices;
